import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Save } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import companyAddCollaboratorModalViewModel, { CollaboratorFormData } from '../../view-model/professional/company/company-add-collaborator-modal-view-model';
import { useAutoCloseModal } from '../../hooks/useAutoCloseModal';

interface AddCollaboratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCollaborator: (data: CollaboratorFormData) => Promise<any>;
}

const CompanyAddCollaboratorModal: React.FC<AddCollaboratorModalProps> = observer(({
  isOpen,
  onClose,
  onAddCollaborator
}) => {
  const { t } = useTranslation();
  const vm = companyAddCollaboratorModalViewModel;

  // Enregistre ce modal pour être fermé automatiquement en cas d'erreur de limite
  useAutoCloseModal(isOpen, onClose);

  // Charger les rôles et permissions au montage du composant
  useEffect(() => {
    if (isOpen) {
      vm.initialize();
    }
  }, [vm, isOpen]);

  // Si le modal n'est pas ouvert, ne rien afficher
  if (!isOpen) return null;

  // Afficher un loader pendant le chargement
  if (vm.isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading', 'Chargement...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            {t('company.addCollaborator.title', 'Ajouter un collaborateur')}
          </h2>
          <button
            onClick={() => onClose()}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={(e) => vm.handleSubmit(e, onAddCollaborator, onClose)} className="p-4">
          <div className="space-y-4">
            {/* Informations personnelles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('company.addCollaborator.firstName', 'Prénom')} *
                </label>
                <input
                    type="text"
                    name="firstName"
                    value={vm.formData.firstName}
                    onChange={vm.handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                        vm.errors.firstName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Jean"
                />
                {vm.errors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{vm.errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('company.addCollaborator.lastName', 'Nom')} *
                </label>
                <input
                    type="text"
                    name="lastName"
                    value={vm.formData.lastName}
                    onChange={vm.handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                        vm.errors.lastName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Dupont"
                />
                {vm.errors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{vm.errors.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('company.addCollaborator.email', 'Email')} *
                </label>
                <input
                    type="email"
                    name="email"
                    value={vm.formData.email}
                    onChange={vm.handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                        vm.errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="jean.dupont@exemple.com"
                />
                {vm.errors.email && (
                    <p className="text-red-500 text-xs mt-1">{vm.errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('company.addCollaborator.phone', 'Téléphone')}
                </label>
                <input
                    type="tel"
                    name="phone"
                    value={vm.formData.phone}
                    onChange={vm.handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="06 12 34 56 78"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('company.addCollaborator.password', 'Mot de passe')} *
                </label>
                <input
                    type="password"
                    name="password"
                    value={vm.formData.password}
                    onChange={vm.handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                        vm.errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                />
                {vm.errors.password && (
                    <p className="text-red-500 text-xs mt-1">{vm.errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('company.addCollaborator.confirmPassword', 'Confirmer le mot de passe')} *
                </label>
                <input
                    type="password"
                    name="confirmPassword"
                    value={vm.formData.confirmPassword}
                    onChange={vm.handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                        vm.errors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                />
                {vm.errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{vm.errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* Sélection de l'entreprise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('company.addCollaborator.company', 'Entreprise')} *
              </label>
              <select
                  name="companyId"
                  value={vm.formData.companyId}
                  onChange={vm.handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                      vm.errors.companyId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary`}
              >
                {vm.availableCompanies.map(company => (
                    <option key={company.id} value={company.id}>{company.businessName}</option>
                ))}
              </select>
              {vm.errors.companyId && (
                  <p className="text-red-500 text-xs mt-1">{vm.errors.companyId}</p>
              )}
            </div>

            {/* Sélection du rôle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('company.addCollaborator.role', 'Rôle')} *
              </label>
              <select
                  name="roleId"
                  value={vm.formData.roleId}
                  onChange={vm.handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                      vm.errors.roleId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary`}
              >
                {vm.availableRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
              {vm.errors.roleId && (
                  <p className="text-red-500 text-xs mt-1">{vm.errors.roleId}</p>
              )}
            </div>

            {/* Afficher/masquer les permissions */}
            <div>
              <button
                  type="button"
                  //onClick={vm.toggleShowPermissions}
                  className="text-primary hover:underline focus:outline-none flex items-center gap-1"
              >
                {vm.showPermissions
                    ? t('company.addCollaborator.hidePermissions', 'Masquer les permissions')
                    : t('company.addCollaborator.showPermissions', 'Personnaliser les permissions')}
              </button>
            </div>

            {/* Permissions */}
            {/*{vm.showPermissions && (

            )}*/}
            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4 mt-2">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">
                {t('company.addCollaborator.permissions', 'Permissions')}
                <span className="ml-2 text-sm font-normal text-gray-500">
                    ({vm.formData.permissionIds.length}/{vm.availablePermissions.length})
                  </span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 max-h-96 overflow-y-auto">
                {Object.entries(vm.groupedPermissions).map(([action, permissions]) => {
                  const allSelected = permissions.every(p => vm.isPermissionSelected(p.id));
                  const someSelected = permissions.some(p => vm.isPermissionSelected(p.id));

                  return (
                      <div key={action} className="bg-gray-50 dark:bg-gray-750 p-3 rounded-lg">
                        <div
                            className="flex items-center cursor-pointer mb-2"
                            onClick={() => vm.togglePermissionGroup(action, permissions)}
                        >
                          <div className={`w-5 h-5 mr-2 rounded flex items-center justify-center border ${
                              allSelected
                                  ? 'bg-primary border-primary'
                                  : someSelected
                                      ? 'bg-primary bg-opacity-30 border-primary'
                                      : 'border-gray-400 dark:border-gray-500'
                          }`}>
                            {allSelected && (
                                <Check className="h-4 w-4 text-white"/>
                            )}
                          </div>
                          <h5 className="font-medium text-gray-800 dark:text-gray-200">
                            {action} <span className="text-xs font-normal text-gray-500">({permissions.length})</span>
                          </h5>
                        </div>

                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-7">
                          {permissions.map((permission) => (
                              <li key={permission.id} className="flex items-center text-sm">
                                <div
                                    className={`w-4 h-4 mr-2 rounded flex items-center justify-center border flex-shrink-0 ${
                                        vm.isPermissionSelected(permission.id)
                                            ? 'bg-primary border-primary'
                                            : 'border-gray-400 dark:border-gray-500'
                                    } cursor-pointer`}
                                    onClick={() => vm.togglePermission(permission.id)}
                                >
                                  {vm.isPermissionSelected(permission.id) && (
                                      <Check className="h-3 w-3 text-white"/>
                                  )}
                                </div>
                                <span
                                    className={`text-gray-600 dark:text-gray-400 cursor-pointer ${
                                        vm.isPermissionSelected(permission.id) ? 'font-medium' : ''
                                    }`}
                                    onClick={() => vm.togglePermission(permission.id)}
                                >
                                {permission.name.split(' ').slice(1).join(' ')}
                              </span>
                              </li>
                          ))}
                        </ul>
                      </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
                type="button"
                onClick={() => onClose()}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {t('company.addCollaborator.cancel', 'Annuler')}
            </button>
            <button
                type="submit"
                disabled={vm.isSubmitting}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16}/>
              {t('company.addCollaborator.save', 'Enregistrer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default CompanyAddCollaboratorModal;
